import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import { useLiveGameConfig } from '@core/js/hooks/games';
import { useTranslations } from '@core/js/hooks/i18n';
import style from '../Game.scss';
import { useCurrencyFormatter } from '@theme/js/hooks/useFormatter/hooks/useCurrencyFormatter';
import { LiveDataTypes } from '../enum';
import { ILiveGameConfig } from '@theme/js/hooks/games/types';
import { BLACK_NUMBERS, MAX_ROULLETE_RESULTS, RED_NUMBERS } from '../constants';
import BaccaratTable from './BaccaratTable';
import { clsx } from '@core/js/utils/classNames.util';
import { useNumberFormatter } from '@core/js/hooks/useFormatter/hooks/useNumberFormatter';

type LiveStreamingVideoProps = {
    liveLaunchAlias: string,
    liveType: string | undefined,
    sizes: { width: number, height: number }
}

type RouletteLiveDataResultsProps = {
    result: string,
    id: number
}

const RouletteLiveDataResults = memo(({ result, id }: RouletteLiveDataResultsProps) => {
    const getRouletteNumberColor = useCallback((rouletteNumber: number): string => {
        if (RED_NUMBERS.includes(rouletteNumber)) { return style.redRouletteNumber; }
        if (BLACK_NUMBERS.includes(rouletteNumber)) { return style.blackRouletteNumber; }
        return style.zeroRouletteNumber;
    }, []);

    const {
        resultNumber: memoResultNumber,
        colorClass: memoColorClass,
        firstItemClass: memoFirstItemClass
    } = useMemo(() => {
        const parsedResultNumber = parseInt(result.split(';')[0]);
        const colorClass = getRouletteNumberColor(parsedResultNumber);
        const firstItemClass = id === 0 ? style.firstRouletteResultItem : '';
        return { resultNumber: parsedResultNumber, colorClass, firstItemClass };
    }, [result, id, getRouletteNumberColor]);

    return (
        <span
            className={clsx([memoColorClass, memoFirstItemClass, style.rouletteResultsItem])}
        >
            {memoResultNumber}
        </span>
    );
});

const LiveDataContainer = ({ liveLaunchAlias, liveType, sizes }: LiveStreamingVideoProps) => {
    const t = useTranslations();
    const formatter = useCurrencyFormatter();
    const liveGameConfig = useLiveGameConfig(liveLaunchAlias) as ILiveGameConfig;
    const {
        limits,
        language,
        seatsInfo,
        results,
        playerCount
    } = liveGameConfig;
    const { formatNumber } = useNumberFormatter();

    const [rouletteResults, setRouletteResults] = useState<string[]>([]);

    useEffect(() => {
        setRouletteResults((prevResults: string[]): string[] => {
            const updatedResults = [...results, ...prevResults];
            const newResults = updatedResults.slice(0, MAX_ROULLETE_RESULTS);
            return newResults;
        });
    }, [results]);

    const getFormattedLimits = useCallback((gameLimits = ''): string => {
        const [minLimit, maxLimit] = gameLimits.match(/\d+/g) || [];

        if (minLimit && maxLimit) {
            return `${formatter.formatKCash(minLimit)} - ${formatter.formatKCash(maxLimit)}`;
        }
        return gameLimits;
    }, [formatter]);

    return (
        <div className={`${style.liveDataContainer} ${sizes.width === 1 ? style.slimContainer : ''}`}>
            {liveType === LiveDataTypes.BACCARAT && <BaccaratTable data={results} />}
            {rouletteResults && liveType === LiveDataTypes.ROULETTE &&
                <div className={style.rouletteResults}>
                    {rouletteResults.map((result, id) => <RouletteLiveDataResults result={result} key={id} id={id} />)}
                </div>
            }
            <div className={style.mainLiveData}>
                {seatsInfo && (
                    <div className={style.seatsInfo} aria-hidden={true}>
                        <span className={style.seatsIcon} />
                        <span>{liveType === LiveDataTypes.BLACKJACK ? seatsInfo : formatNumber(playerCount)}</span>
                    </div>
                )}

                {language && (
                    <div className={style.liveLanguage}>
                        <div className={style.liveLanguageContainer}>
                            {t(`playtech.mobile.system.liveData.language.${language}`)}
                        </div>
                    </div>
                )}

                {limits && (
                    <div className={style.liveLimits}>
                        <span className={style.limitsIcon} />
                        {getFormattedLimits(limits)}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LiveDataContainer;
